import { createContext } from '@shared/utils/createContext';
import type PrintPriceCalculator from '@shared/utils/print-price/PrintPriceCalculator';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import type { ReactNode } from 'react';
import React, { useMemo, useRef } from 'react';
import { useProductToUIMapping } from '../useProductToUIMapping';
import type { Product } from '../product.types';
import { useProductAttributeStates } from './state/useProductAttributeStates';
import { usePrintPrices } from '@apps/card/hooks/usePrintPrices';
import type { CurrencyCode } from '@shared/hooks/useGeoLocale';

type StationeryProductPricingContextType = {
  products: StationeryProduct[];
  activeProduct: StationeryProduct & Product;
  priceCalculator: PrintPriceCalculator;
  attributeStates: ReturnType<typeof useProductAttributeStates>;
  orderCurrencyISO4217?: CurrencyCode;
};

const [Provider, useStationeryProductPricingContext] = createContext<StationeryProductPricingContextType>({
  name: 'StationeryProductPricingContext',
});

// Display order for the product choice surfaces (PDP selector, customizer
// toggle): paper first, then every digital variant, magnet last. The backend
// returns the template's products in link order, which is arbitrary — magnet
// was linked to long-existing templates, so unsorted it can render between
// paper and digital. All three digital product types must sit here: 'digital'
// is the legacy free-ecard dummy (FREE_DIGITAL_DUMMY_PRODUCT), 'digitalCard'
// is premium digital, 'basicDigitalV2' the free-v2 dummy — any omitted one
// falls to the unknown bucket and sorts AFTER magnet. Unknown types sort last,
// keeping their relative order (Array.sort is stable), which is fine for the
// single-product paper templates (napkin/sign) that never co-occur with magnet.
const PRODUCT_TYPE_DISPLAY_ORDER: Record<string, number> = {
  stationeryCard: 0,
  digital: 1,
  digitalCard: 2,
  basicDigitalV2: 3,
  magnet: 4,
};

// Exported for unit testing the ordering directly (the provider itself pulls in
// the whole pricing / UI-mapping graph, which the sort does not depend on).
export const byDisplayOrder = (a: Pick<StationeryProduct, 'productType'>, b: Pick<StationeryProduct, 'productType'>) =>
  (PRODUCT_TYPE_DISPLAY_ORDER[a.productType] ?? 99) - (PRODUCT_TYPE_DISPLAY_ORDER[b.productType] ?? 99);

type StationeryProductPricingContextProviderProps = {
  products: StationeryProduct[];
  activeProductId: string;
  children: ReactNode;
  orderCurrencyConversionData?: unknown;
  orderCurrencyISO4217?: string | null;
};

const StationeryProductPricingContextProvider = (props: StationeryProductPricingContextProviderProps) => {
  const { products: unorderedProducts, children, activeProductId, orderCurrencyConversionData, orderCurrencyISO4217 } = props;
  const { getProductWithUIMapping } = useProductToUIMapping();

  const products = useMemo(() => [...unorderedProducts].sort(byDisplayOrder), [unorderedProducts]);

  const priceCalculator = usePrintPrices(products, orderCurrencyConversionData);
  const activeProduct = products.find(p => p.id === activeProductId);
  const attributeStates = useProductAttributeStates();
  const previousActiveProduct = useRef(activeProductId);

  // Destroy Attribute State to Draft State connections when switching products
  if (activeProductId !== previousActiveProduct.current) {
    previousActiveProduct.current = activeProductId;
    attributeStates.destroyLinks();
  }

  if (!activeProduct) {
    throw new Error('Product not avaliable');
  }
  const mappedActiveProduct = getProductWithUIMapping(activeProduct);

  if (!mappedActiveProduct) {
    throw new Error('Product config not defined');
  }

  return (
    <Provider
      value={{
        products,
        activeProduct: mappedActiveProduct,
        priceCalculator,
        attributeStates,
        orderCurrencyISO4217: orderCurrencyISO4217 as CurrencyCode | undefined,
      }}
    >
      {children}
    </Provider>
  );
};

export { StationeryProductPricingContextProvider, useStationeryProductPricingContext };
